// utils/elementFormat/number.js
//
// How a number element RENDERS: the `decimals` and `leadingZeros` options a
// number/decimal/integer element may carry (docs/plans/data-types.md, the
// frozen wire contract), applied to one stored value.
//
// PURE AND DEPENDENCY-FREE ON PURPOSE. This file is vendored byte-identical
// into keepr-web at `src/utils/elementFormat/number.js`, the way
// utils/formula/formula.js and utils/units/ are, so a value spells the same
// on the item form, in the list, in a display title and in an export.
// Nothing here may require() anything but its sibling `./fraction.js`
// (vendored the same way), and nothing here may know about Mongo, Boom or
// the request. MIRRORED in keepr-web — change them together.
//
// Six rules, and no seventh:
//
//   decimals      0–6 fixed digits, trailing zeros KEPT (12.5 at 2 -> '12.50',
//                 12 at 2 -> '12.00'). Rounding is HALF AWAY FROM ZERO — the
//                 rule a person reading a form expects (-2.5 at 0 -> '-3'),
//                 not JavaScript's Math.round, which rounds half toward
//                 +Infinity (-2.5 -> -2). Absent = the value's own spelling.
//   leadingZeros  2–20 integer digits to pad to, SIGN FIRST: -7 at 3 ->
//                 '-007', 12.5 at 4 -> '0012.5'. Padding never truncates a
//                 longer number. Absent = off.
//   prefix        up to 16 characters written at the VERY FRONT of the
//                 finished text, sign included: 42 with prefix 'INV-' and
//                 leadingZeros 6 -> 'INV-000042'. Absent or '' = off.
//   thousands     true groups the whole-number digits in threes with ','
//                 (en-US): 1234567.5 -> '1,234,567.5'. Absent = off.
//   percent       true writes '%' straight after the digits: 12.5 ->
//                 '12.5%'. The stored value IS the number shown (the
//                 owner's ruling D9, keepr-web docs/plans/
//                 data-types-second-pass.md §3.8): 12.5 means 12.5 %, never
//                 0.125, so nothing here multiplies — a formula or a
//                 threshold reads the same 12.5 a person typed.
//   fractions     true spells a value near a half, third, quarter or eighth
//                 as that fraction (KPR-1025): 1.5 -> '1 1/2', 0.75 ->
//                 '3/4', 0.33 -> '1/3' (within 0.005 — ./fraction.js says
//                 why that tolerance). A value near none keeps the spelling
//                 the other rules give it (0.1 -> '0.1'). Grouped with
//                 `thousands` ('1,234 1/2'). Never on an identifier or a
//                 percent (the write path refuses the pairing; a stored pair
//                 renders as if `fractions` were absent). The STORED value is
//                 the number; this is how it reads.
//
// `prefix` is a plain number-format key, not a sequence key, for the same
// reason `leadingZeros` is: server-allocated sequence numbers
// (utils/sequenceUtils.js) store the RAW NUMBER and let this file do the
// spelling, so there must be exactly one way to pad and exactly one way to
// prefix — whether the number came from a counter or from a person typing
// a PO number by hand. A negative value keeps its sign after the prefix
// ('INV--007'), which is honest rather than pretty; nothing that carries a
// prefix is expected to go below zero, and hiding the sign would be worse.
//
// An IDENTIFIER is never grouped and is never a percentage. A padded or
// prefixed number ('0007', 'WO-417') names a thing, so `thousands` and
// `percent` are IGNORED beside `leadingZeros` or `prefix` — the card write
// path refuses those pairings (400 invalid_options), and a stored pair that
// predates the rule renders as the identifier it was. Grouping is done on
// this file's own digits rather than by toLocaleString, which stops at three
// fraction digits when `decimals` is absent (1234.5678 -> '1,234.568') and
// would print a value the field does not hold. (The display template's
// `|thousands` and `|percent` formatters are a different thing: the first is
// toLocaleString, the second reads a FRACTION and multiplies by 100.)
//
// `step` lives here too, though it renders nothing: the increment buttons'
// and the slider's step must fit the element's decimals, and the card write
// path and the card editor must quote one rule (stepFits).
//
// Values outside the documented ranges are IGNORED rather than clamped: the
// card write path refuses them (400 invalid_options), so anything that
// reaches here out of range is legacy or hand-edited data, and rendering it
// as if the option were absent is the one answer that cannot surprise.

'use strict';

const Fraction = require('./fraction');

// The option ranges, exported so the validators on both sides of the wire
// quote one set of numbers.
const MAX_DECIMALS = 6;
const MIN_LEADING_ZEROS = 2;
const MAX_LEADING_ZEROS = 20;
// Long enough for 'INVOICE-' or 'WO-2026-' and short enough that the prefix
// can never be the value. The card write path refuses a longer one.
const MAX_PREFIX = 16;
// The largest step the increment buttons and the slider take. A step past a
// million is a field that wants typing, not stepping.
const MAX_STEP = 1000000;

const isIntegerInRange = (v, min, max) => Number.isInteger(v) && v >= min && v <= max;

/** The element's fixed-decimals setting, or null when it has none. */
function decimalsOf(options) {
	const value = options ? options.decimals : undefined;
	return isIntegerInRange(value, 0, MAX_DECIMALS) ? value : null;
}

/** The element's integer-digit padding, or null when it has none. */
function leadingZerosOf(options) {
	const value = options ? options.leadingZeros : undefined;
	return isIntegerInRange(value, MIN_LEADING_ZEROS, MAX_LEADING_ZEROS) ? value : null;
}

/**
 * The element's literal prefix, or '' when it has none. Anything that is not
 * a string, and anything longer than MAX_PREFIX, reads as absent — the same
 * posture the two numeric options take, and for the same reason: the write
 * path refuses it, so a value that reaches here out of range is legacy or
 * hand-edited data and rendering it as if the option were absent is the one
 * answer that cannot surprise.
 */
function prefixOf(options) {
	const value = options ? options.prefix : undefined;
	if (typeof value !== 'string' || value === '' || value.length > MAX_PREFIX) return '';
	return value;
}

/** Does the element spell an identifier (padded digits or a prefix)? */
function isIdentifier(options) {
	return leadingZerosOf(options) !== null || prefixOf(options) !== '';
}

/** Is the value grouped in thousands? Only `true` counts, and never on an identifier. */
function thousandsOf(options) {
	return !!options && options.thousands === true && !isIdentifier(options);
}

/** Is the value shown as a percent? Only `true` counts, and never on an identifier. */
function percentOf(options) {
	return !!options && options.percent === true && !isIdentifier(options);
}

/**
 * Is the value spelled as a fraction? Only `true` counts, and never on an
 * identifier or a percent.
 */
function fractionsOf(options) {
	return !!options && options.fractions === true && !isIdentifier(options) && options.percent !== true;
}

/**
 * How many digits a number has after the point, in plain notation: 0.25 -> 2,
 * 1e-7 -> 7, 1.5e-7 -> 8, 12 -> 0. null for anything that is not finite.
 */
function decimalPlaces(num) {
	if (typeof num !== 'number' || !Number.isFinite(num)) return null;
	const text = String(Math.abs(num));
	const e = text.indexOf('e');
	const mantissa = e === -1 ? text : text.slice(0, e);
	const dot = mantissa.indexOf('.');
	const places = dot === -1 ? 0 : mantissa.length - dot - 1;
	return e === -1 ? places : Math.max(0, places - Number(text.slice(e + 1)));
}

/**
 * stepFits(step, decimals) -> boolean
 *
 * A step is a positive number no larger than MAX_STEP whose digits after the
 * point fit the element's decimals: 0.25 needs 2, and a whole-number element
 * (decimals 0) steps by whole numbers. With no decimals set, the bound is
 * MAX_DECIMALS — the finest the platform stores.
 */
function stepFits(step, decimals) {
	if (typeof step !== 'number' || !Number.isFinite(step) || step <= 0 || step > MAX_STEP) return false;
	const limit = isIntegerInRange(decimals, 0, MAX_DECIMALS) ? decimals : MAX_DECIMALS;
	return decimalPlaces(step) <= limit;
}

/**
 * The element's step, or 1 when it has none (or one the write path would
 * refuse — legacy or hand-edited data reads as the option absent). Judged
 * against the element's own `decimals`; a reader that knows more (an
 * `integer` element is decimals 0) passes `decimals` itself.
 */
function stepOf(options, decimals) {
	const value = options ? options.step : undefined;
	const places = decimals === undefined ? decimalsOf(options) : decimals;
	return stepFits(value, places) ? value : 1;
}

/** Groups the whole-number digits of an already-formatted decimal string. */
function groupThousands(text) {
	const negative = text.charAt(0) === '-';
	const body = negative ? text.slice(1) : text;
	const dot = body.indexOf('.');
	const integerPart = dot === -1 ? body : body.slice(0, dot);
	const rest = dot === -1 ? '' : body.slice(dot);
	let grouped = '';
	for (let i = 0; i < integerPart.length; i += 1) {
		if (i > 0 && (integerPart.length - i) % 3 === 0) grouped += ',';
		grouped += integerPart.charAt(i);
	}
	return `${negative ? '-' : ''}${grouped}${rest}`;
}

/**
 * The magnitude as a decimal string with exactly `decimals` digits after the
 * point, rounded half away from zero.
 *
 * The digits are assembled from the ROUNDED INTEGER (`Math.round(magnitude *
 * 10^decimals)`) rather than from a second float division, so the string
 * never picks up binary dust on the way back (2.675 at 2 stays "2.68", not
 * "2.6799…"). The scaling itself is still a float multiply, so a value that
 * already sits on a binary boundary rounds the way the multiply lands
 * (1.005 * 100 is 100.49999…, hence "1.00") — the same answer toFixed gives.
 * Past MAX_SAFE_INTEGER that integer stops being exact and toFixed is the
 * better answer.
 */
function fixedDigits(num, decimals) {
	const negative = num < 0;
	const magnitude = Math.abs(num);
	const factor = Math.pow(10, decimals);
	const scaled = Math.round(magnitude * factor);
	let text;
	if (Number.isSafeInteger(scaled)) {
		const digits = String(scaled).padStart(decimals + 1, '0');
		text = decimals === 0
			? digits
			: `${digits.slice(0, digits.length - decimals)}.${digits.slice(digits.length - decimals)}`;
	} else {
		text = magnitude.toFixed(decimals);
	}
	// -0.4 at 0 decimals is 0, not '-0': a sign survives only when a digit does.
	return negative && /[1-9]/.test(text) ? `-${text}` : text;
}

/** Pads the integer part of an already-formatted decimal string to `digits`. */
function padIntegerPart(text, digits) {
	const negative = text.charAt(0) === '-';
	const body = negative ? text.slice(1) : text;
	const dot = body.indexOf('.');
	const integerPart = dot === -1 ? body : body.slice(0, dot);
	const rest = dot === -1 ? '' : body.slice(dot);
	if (integerPart.length >= digits) return text;
	return `${negative ? '-' : ''}${integerPart.padStart(digits, '0')}${rest}`;
}

/**
 * formatNumber(value, options) -> string
 *
 * '' for anything that is not a number: null, undefined, '', a blank string,
 * NaN/Infinity, an object, a boolean. A numeric STRING is accepted (element
 * values are stored as the client submitted them, so a legacy element may
 * hold '12.5') and renders as the number it spells.
 */
function formatNumber(value, options) {
	if (value === null || value === undefined) return '';
	let num;
	if (typeof value === 'number') {
		num = value;
	} else if (typeof value === 'string') {
		const trimmed = value.trim();
		if (!trimmed) return '';
		num = Number(trimmed);
	} else {
		return '';
	}
	if (!Number.isFinite(num)) return '';

	// A value near a fraction reads as one; any other keeps the rules below.
	if (fractionsOf(options)) {
		const fraction = Fraction.formatFraction(num, { group: thousandsOf(options) });
		if (fraction !== null) return fraction;
	}

	const decimals = decimalsOf(options);
	let text = decimals === null ? String(num) : fixedDigits(num, decimals);

	const leadingZeros = leadingZerosOf(options);
	// Exponent notation (1e+21, 5e-7) has no integer part to pad — String()
	// only produces it well outside the range a padded identifier lives in.
	if (leadingZeros !== null && text.indexOf('e') === -1 && text.indexOf('E') === -1) {
		text = padIntegerPart(text, leadingZeros);
	}
	// Grouping and the percent sign never meet an identifier (thousandsOf /
	// percentOf answer false beside padding or a prefix). Exponent notation
	// has no digits to group, as it has none to pad.
	if (thousandsOf(options) && text.indexOf('e') === -1 && text.indexOf('E') === -1) {
		text = groupThousands(text);
	}
	if (percentOf(options)) text += '%';
	// Last, so the prefix is never something the padding can reach into.
	const prefix = prefixOf(options);
	return prefix ? prefix + text : text;
}

/**
 * The number a value rounds to at `decimals` (half away from zero), as a
 * NUMBER — what the write path stores. Returns the value untouched when the
 * element sets no decimals.
 */
function roundToDecimals(num, decimals) {
	if (!Number.isFinite(num)) return num;
	if (!isIntegerInRange(decimals, 0, MAX_DECIMALS)) return num;
	const rounded = Number(fixedDigits(num, decimals));
	return Number.isFinite(rounded) ? rounded : num;
}

module.exports = {
	MAX_DECIMALS,
	MIN_LEADING_ZEROS,
	MAX_LEADING_ZEROS,
	MAX_PREFIX,
	MAX_STEP,
	formatNumber,
	decimalsOf,
	leadingZerosOf,
	prefixOf,
	isIdentifier,
	thousandsOf,
	percentOf,
	fractionsOf,
	decimalPlaces,
	stepFits,
	stepOf,
	// Exported for the write path, which rounds a value to the element's
	// decimals BEFORE storing it: the stored number and the rendered one must
	// agree digit for digit.
	roundToDecimals,
};
